import { Button, Collapse, Flex, Grid, Paper, Select, TextInput } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { BroomIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IReference } from "../../store/General"
import { DropShippingFilters } from "./dropshipping-filters"

type Props = {
  opened: boolean
  filters: DropShippingFilters
  setFilter: <K extends keyof DropShippingFilters>(key: K, value: DropShippingFilters[K]) => void
  clearFilters: () => void
  orderStatus: IReference[]
  paymentStatus: IReference[]
  deliveryNoteStatus: IReference[]
  orderTypes: IReference[]
  orderPurchaseTypes: IReference[]
  onSearch?: () => void
}

export function DropShippingFilterPanel({
  opened, filters, setFilter, clearFilters,
  orderStatus, paymentStatus, deliveryNoteStatus, orderTypes, orderPurchaseTypes, onSearch,
}: Props) {
  const { t } = useTranslation(["supplierSales", "common"])

  return (
    <Collapse expanded={opened}>
      <Paper p="md" mb="sm" withBorder radius="md">
        <Grid>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput label={t("supplierSales:dropshipping.filters.soNumber")} value={filters.salesCode} onChange={(e) => setFilter("salesCode", e.target.value)} placeholder={t("supplierSales:dropshipping.filters.search")} size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <DatePickerInput label={t("supplierSales:dropshipping.filters.date")} type="range" value={filters.createdAt} onChange={(v) => setFilter("createdAt", v)} placeholder={t("common:filter.selectPlaceholder")} valueFormat="YYYY-MM-DD" size="sm" clearable />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select label={t("supplierSales:dropshipping.filters.type")} value={filters.orderType} onChange={(v) => setFilter("orderType", v)} data={orderTypes.map((o) => ({ label: o.name, value: o.code }))} placeholder={t("common:filter.selectPlaceholder")} clearable size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("supplierSales:dropshipping.filters.status")}
              value={filters.orderStatus}
              onChange={(v) => setFilter("orderStatus", v)}
              data={orderStatus.filter((o) => !["DRAFT", "REGISTERED", "REVIEW_REJECTED"].includes(o.code)).map((o) => ({ label: o.sentName!, value: o.code }))}
              placeholder={t("common:filter.selectPlaceholder")} clearable size="sm"
            />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput label={t("supplierSales:dropshipping.filters.partnerName")} value={filters.receiverBusinessPartnerRefCodeBusinessName} onChange={(e) => setFilter("receiverBusinessPartnerRefCodeBusinessName", e.target.value)} placeholder={t("supplierSales:dropshipping.filters.search")} size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput label={t("supplierSales:dropshipping.filters.receiverBranch")} value={filters.receiverBranchName} onChange={(e) => setFilter("receiverBranchName", e.target.value)} placeholder={t("supplierSales:dropshipping.filters.search")} size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <DatePickerInput label={t("supplierSales:dropshipping.filters.deliveryDate")} type="range" value={filters.deliveryDate} onChange={(v) => setFilter("deliveryDate", v)} placeholder={t("common:filter.selectPlaceholder")} valueFormat="YYYY-MM-DD" size="sm" clearable />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select label={t("supplierSales:dropshipping.filters.deliveryStatus")} value={filters.deliveryNoteStatus} onChange={(v) => setFilter("deliveryNoteStatus", v)} data={deliveryNoteStatus.map((o) => ({ label: o.name, value: o.code }))} placeholder={t("common:filter.selectPlaceholder")} clearable size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select label={t("supplierSales:dropshipping.filters.paymentStatus")} value={filters.paymentStatus} onChange={(v) => setFilter("paymentStatus", v)} data={paymentStatus.map((o) => ({ label: o.name, value: o.code }))} placeholder={t("common:filter.selectPlaceholder")} clearable size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput label={t("supplierSales:dropshipping.filters.poNumber")} value={filters.purchaseCode} onChange={(e) => setFilter("purchaseCode", e.target.value)} placeholder={t("supplierSales:dropshipping.filters.search")} size="sm" />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select label={t("supplierSales:dropshipping.filters.poType")} value={filters.purchaseType} onChange={(v) => setFilter("purchaseType", v)} data={orderPurchaseTypes.map((o) => ({ label: o.name, value: o.code }))} placeholder={t("common:filter.selectPlaceholder")} clearable size="sm" />
          </Grid.Col>
        </Grid>
        <Flex justify="flex-end" gap="sm" mt="md">
          <Button variant="light" color="gray" size="sm" radius="md" leftSection={<BroomIcon size={16} />} onClick={clearFilters}>{t("supplierSales:dropshipping.filters.clear")}</Button>
          <Button variant="filled" color="dark" size="sm" radius="md" leftSection={<MagnifyingGlassIcon size={16} />} onClick={onSearch}>{t("supplierSales:dropshipping.filters.search")}</Button>
        </Flex>
      </Paper>
    </Collapse>
  )
}
